import React, { useState, useEffect } from 'react';
import { ArrowRight, Globe, Split, Sparkles, CheckSquare, Users, Phone, Calendar, Tag, Brain, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import SignIn from './SignIn';
import OnboardingModal from '../components/OnboardingModal';
import VideoModal from '../components/VideoModal';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 }
};

const slideUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.12
    }
  }
};

const staggerFast = {
  visible: {
    transition: {
      staggerChildren: 0.06
    }
  }
};

const rotateIn = {
  hidden: { opacity: 0, rotate: -5, scale: 0.95 },
  visible: { 
    opacity: 1, 
    rotate: 0, 
    scale: 1,
    transition: { 
      type: "spring", 
      stiffness: 200, 
      damping: 20 
    } 
  }
};

// Feature Card component with enhanced animations and styling
const FeatureCard = ({ icon: Icon, title, description, delay = 0 }) => (
  <motion.div
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-20%" }}
    variants={{
      hidden: { opacity: 0, y: 20, scale: 0.95 },
      visible: { 
        opacity: 1, 
        y: 0, 
        scale: 1,
        transition: { 
          type: "spring", 
          delay,
          duration: 0.7 
        } 
      }
    }}
    whileHover={{ 
      y: -8, 
      boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" 
    }}
    className="p-6 bg-white rounded-xl border-2 border-dark overflow-hidden relative group"
  >
    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    <div className="absolute -inset-2 bg-gradient-radial from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    
    <div className="relative flex items-start gap-3">
      <motion.div 
        initial={{ scale: 1 }}
        whileHover={{ scale: 1.1, rotate: 5 }}
        className="p-2 bg-primary/10 rounded-lg"
      >
        <Icon className="h-6 w-6 text-primary" />
      </motion.div>
      <div>
        <h4 className="text-xl font-semibold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-dark to-dark/80">{title}</h4>
        <p className="text-gray-600">{description}</p>
      </div>
    </div>
    
    <motion.div 
      className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-secondary to-primary bg-[length:200%_auto]" 
      initial={{ scaleX: 0, opacity: 0 }}
      whileInView={{ scaleX: 1, opacity: 1 }}
      transition={{ duration: 0.8, delay: delay + 0.3 }}
      viewport={{ once: true }}
    />
  </motion.div>
);

const LandingPage = () => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  // Handle scroll for parallax effects
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen overflow-hidden relative">
      {/* Ambient background elements */}
      <div className="fixed inset-0 bg-gradient-to-b from-white via-primary/5 to-white opacity-40 -z-10" />
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(69,183,135,0.1),transparent_70%)] -z-10" />
      <div 
        className="fixed w-full h-full bg-[url('https://images.unsplash.com/photo-1557683316-973673baf926')] bg-fixed bg-no-repeat bg-cover opacity-[0.02] -z-10"
        style={{ 
          filter: "blur(30px) contrast(120%)",
        }}
      />
      
      {/* Animated gradient orbs */}
      <motion.div 
        className="fixed w-[500px] h-[500px] rounded-full bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/5 -z-10"
        animate={{
          x: ["-20%", "10%", "-10%"],
          y: ["10%", "30%", "15%"],
          scale: [1, 1.1, 0.9],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          repeatType: "reverse",
        }}
        style={{
          filter: "blur(100px)",
          left: "20%",
          top: "10%",
        }}
      />
      
      <motion.div 
        className="fixed w-[600px] h-[600px] rounded-full bg-gradient-to-r from-accent/5 via-secondary/5 to-accent/10 -z-10"
        animate={{
          x: ["5%", "-10%", "15%"],
          y: ["5%", "-10%", "0%"],
          scale: [1, 0.9, 1.1],
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          repeatType: "reverse",
        }}
        style={{
          filter: "blur(100px)",
          right: "10%",
          bottom: "5%",
        }}
      />

      {/* Header */}
      <motion.header 
        className="fixed w-full bg-white/90 backdrop-blur-md z-50 border-b border-gray-100"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <motion.div 
              className="flex items-center"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="relative">
                <Split className="h-8 w-8 text-primary relative z-10" />
                <motion.div 
                  className="absolute -inset-1 rounded-full bg-primary/10"
                  animate={{ 
                    scale: [1, 1.2, 1],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                />
              </div>
              <span className="ml-2 text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-dark">Double Me</span>
            </motion.div>
            <motion.button
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              onClick={() => setShowLoginModal(true)}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium relative overflow-hidden group"
            >
              <span className="relative z-10">Log In</span>
              <motion.div 
                className="absolute bottom-0 left-0 w-full h-0.5 bg-primary origin-left"
                initial={{ scaleX: 0 }}
                whileHover={{ scaleX: 1 }}
                transition={{ duration: 0.3 }}
              />
            </motion.button>
          </div>
        </div>
      </motion.header>

      {/* Hero Section */}
      <section className="pt-40 pb-32 relative overflow-hidden">
        <motion.div 
          className="absolute w-full h-full bg-gradient-to-b from-primary/5 via-primary/10 to-transparent -z-10"
          style={{ 
            transform: 'translateY(${scrollY * 0.1}px)',
          }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center"
            initial="hidden"
            animate="visible"
            variants={stagger}
          >
            <motion.div variants={fadeIn} transition={{ duration: 0.8 }}>
              <motion.h1 
                className="text-6xl md:text-8xl font-bold font-display tracking-tight text-balance mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: "easeOut" }}
              >
                <span className="relative inline-block">
                  You 
                  <motion.div 
                    className="absolute -inset-1 rounded-lg bg-primary/5 -z-10"
                    animate={{ 
                      scale: [1, 1.05, 1],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      repeatType: "reverse"
                    }}
                  />
                </span>{" "}
                <span className="relative">
                  Can't 
                  <motion.div 
                    className="absolute -inset-1 -z-10"
                    animate={{
                      background: [
                        "radial-gradient(circle at center, rgba(255,155,80,0.2) 0%, transparent 70%)",
                        "radial-gradient(circle at center, rgba(255,155,80,0) 0%, transparent 70%)",
                      ]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatType: "reverse"
                    }}
                  />
                </span>{" "}
                <span className="relative">
                  Be 
                </span>{" "}
                <span className="relative">
                  Everywhere
                </span>{"."}
                <br />
                <span className="relative inline-block">
                  <span className="bg-gradient-to-br from-primary via-secondary to-accent bg-clip-text text-transparent animate-gradient bg-[length:200%_auto]">
                    But 
                  </span>
                  <motion.div 
                    className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 blur-xl -z-10"
                    animate={{
                      opacity: [0.5, 0.8, 0.5],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatType: "reverse"
                    }}
                  />
                </span>{" "}
                <span className="relative inline-block">
                  <span className="bg-gradient-to-r from-secondary to-accent bg-clip-text text-transparent animate-gradient bg-[length:200%_auto]">
                    You Can Sound Like It.
                  </span>
                  <motion.div 
                    className="absolute inset-0 bg-gradient-to-r from-secondary/20 to-accent/20 blur-xl -z-10"
                    animate={{
                      opacity: [0.5, 0.8, 0.5],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatType: "reverse",
                      delay: 0.5
                    }}
                  />
                </span>
              </motion.h1>
            </motion.div>
            
            <motion.p 
              className="text-xl md:text-2xl text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed"
              variants={slideUp}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              In less than two minutes, create a version of yourself that picks up the phone, holds the conversation, and follows through…with your voice, your context, and your standards.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-6 justify-center items-center"
              variants={stagger}
            >
              <motion.button 
                onClick={() => setShowOnboardingModal(true)}
                className="bg-gradient-to-r from-primary to-primary/90 text-white font-display px-8 py-3 rounded-full transition-all border-2 border-dark shadow-retro hover:shadow-none hover:-translate-y-1 relative overflow-hidden group"
                variants={slideUp}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <motion.div 
                  className="absolute inset-0 bg-gradient-conic from-white/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                  animate={{ 
                    rotate: [0, 360],
                  }}
                  transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                />
                <span className="relative z-10 flex items-center">
                  <Sparkles className="w-5 h-5 mr-2" />
                  <span>Start With One Double</span>
                  <ArrowRight className="ml-2 h-5 w-5" />
                </span>
              </motion.button>
              
              <motion.button 
                className="bg-white text-dark font-display px-8 py-3 rounded-full transition-all border-2 border-dark shadow-retro hover:shadow-none hover:-translate-y-1 relative overflow-hidden group"
                variants={slideUp}
                onClick={() => setShowVideoModal(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <motion.div 
                  className="absolute inset-0 bg-gradient-shine bg-[length:400%_400%] opacity-0 group-hover:opacity-20 transition-opacity"
                  animate={{ 
                    backgroundPosition: ['0% 0%', '100% 100%'],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    repeatType: "mirror"
                  }}
                />
                <span className="relative z-10 flex items-center">
                  <Globe className="w-5 h-5 mr-2" />
                  <span>See How Double Me Works</span>
                </span>
              </motion.button>
            </motion.div>
          </motion.div>
          
          <motion.div 
            className="mt-24 relative"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.div 
              className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent z-10"
              variants={fadeIn}
              transition={{ duration: 1.5 }}
            />
            <motion.div
              className="relative rounded-2xl overflow-hidden border-2 border-dark shadow-retro"
              variants={rotateIn}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <motion.div 
                className="absolute -inset-2 bg-gradient-to-r from-primary/20 via-secondary/20 to-accent/20 blur-xl opacity-0"
                animate={{ opacity: [0, 0.5, 0] }}
                transition={{ duration: 5, repeat: Infinity, repeatType: "mirror" }}
              />
              <img
                src="https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&q=80"
                alt="Platform preview"
                className="w-full rounded-2xl relative z-0"
              />
              <motion.div 
                className="absolute inset-0 bg-gradient-to-t from-white/80 to-transparent"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1, duration: 1 }}
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* What Double Me Does Section */}
      <section className="py-24 bg-white relative">
        <div className="absolute inset-0 bg-gradient-conic from-primary/5 via-transparent to-transparent opacity-30" />
        <motion.div 
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
        >
          <motion.div 
            className="text-center mb-16"
            variants={fadeIn}
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold mb-6 relative inline-block">
                <motion.div
                  className="absolute -inset-3 rounded-lg -z-10"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 }}
                  style={{
                    background: "radial-gradient(circle, rgba(255, 211, 63, 0.2) 0%, rgba(255, 211, 63, 0) 70%)"
                  }}
                />
                <Sparkles className="inline-block w-8 h-8 mr-2 text-secondary" />
                <span>Here's Exactly What You Can Do With Double Me</span>
              </h2>
            </motion.div>
            <motion.p 
              className="text-xl text-gray-600 max-w-3xl mx-auto"
              variants={fadeIn}
              transition={{ delay: 0.2 }}
            >
              Call handling. Scheduling. Summarizing. CRM. Follow-ups. Every double is built with these tools, out of the box.
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            <FeatureCard
              icon={Users}
              title="Create Unlimited Doubles"
              description="Spin up as many versions of yourself as you need—each with its own tone, context, and role."
              delay={0.1}
            />
            
            <FeatureCard
              icon={Phone}
              title="Take Inbound or Make Outbound Calls"
              description="Each double has a dedicated number so it can hold real conversations on your behalf."
              delay={0.2}
            />
            
            <FeatureCard
              icon={Calendar}
              title="Share a Scheduling Link"
              description="Every double includes its own booking page. Send the link, let people pick a slot, and your double does the rest."
              delay={0.3}
            />

            <FeatureCard
              icon={Brain}
              title="Instant Summaries"
              description="Every call is auto‑summarized with clear highlights—catch up on hours of talk in minutes."
              delay={0.4}
            />

            <FeatureCard
              icon={CheckSquare}
              title="Triaged Follow‑Ups"
              description="Follow‑ups are generated automatically—keep, assign, or archive with two clicks."
              delay={0.5}
            />

            <FeatureCard
              icon={Zap}
              title="Built‑In CRM Sync"
              description="One system, one memory. Every double updates your contacts automatically."
              delay={0.6}
            />
          </div>
        </motion.div>
      </section>

      {/* Value Stack Section */}
      <section className="py-24 relative overflow-hidden">
        <motion.div 
          className="absolute inset-0 bg-gradient-radial from-primary/5 via-primary/10 to-transparent -z-10"
          animate={{
            opacity: [0.5, 0.8, 0.5],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1557683316-973673baf926')] bg-fixed bg-no-repeat bg-cover opacity-5 -z-10" />
        
        <motion.div 
          className="absolute w-full h-[90%] left-0 right-0 bottom-0 -z-10"
          style={{
            backgroundImage: "linear-gradient(to top, rgba(255,255,255,1), rgba(255,255,255,0))",
          }}
        />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="max-w-3xl mx-auto text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
          >
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <h2 className="text-4xl font-bold mb-6 relative inline-block">
                <motion.div
                  className="absolute -inset-3 rounded-lg -z-10"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  style={{
                    background: "radial-gradient(circle, rgba(69, 183, 135, 0.2) 0%, rgba(69, 183, 135, 0) 70%)"
                  }}
                />
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-accent">This is what comes after the calendar.</span>
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                A way to be in more places, without being stretched thin.
              </p>
              <p className="text-xl text-gray-600 mb-12">
                This isn't a scheduling tool. It's a distributed layer of presence—one that listens, speaks, and learns for you. Every double you launch gives you more leverage and less repetition.
              </p>
            </motion.div>

            <div className="text-xl text-gray-600 space-y-8 mt-16">
              <motion.div 
                className="p-6 bg-white rounded-xl border-2 border-dark shadow-retro relative overflow-hidden group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                whileHover={{ y: -5 }}
              >
                <motion.div 
                  className="absolute inset-0 bg-gradient-radial from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500"
                  animate={{
                    scale: [1, 1.1, 1],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                />
                <p className="relative z-10"><strong>Filtering the noise?</strong> Doubles take the early meetings so only the signal reaches you.</p>
              </motion.div>
              
              <motion.div 
                className="p-6 bg-white rounded-xl border-2 border-dark shadow-retro relative overflow-hidden group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
                whileHover={{ y: -5 }}
              >
                <motion.div 
                  className="absolute inset-0 bg-gradient-radial from-secondary/5 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500"
                  animate={{
                    scale: [1, 1.1, 1],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    repeatType: "reverse",
                    delay: 1
                  }}
                />
                <p className="relative z-10"><strong>Hiring fast?</strong> Your double screens candidates and returns a clean, structured summary.</p>
              </motion.div>
              
              <motion.div 
                className="p-6 bg-white rounded-xl border-2 border-dark shadow-retro relative overflow-hidden group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.5 }}
                whileHover={{ y: -5 }}
              >
                <motion.div 
                  className="absolute inset-0 bg-gradient-radial from-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500"
                  animate={{
                    scale: [1, 1.1, 1],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    repeatType: "reverse",
                    delay: 2
                  }}
                />
                <p className="relative z-10"><strong>Busy day?</strong> Your doubles take the calls you can't—so nothing falls through.</p>
              </motion.div>
              
              <motion.div 
                className="p-6 bg-white rounded-xl border-2 border-dark shadow-retro relative overflow-hidden group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.6 }}
                whileHover={{ y: -5 }}
              >
                <motion.div 
                  className="absolute inset-0 bg-gradient-radial from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500"
                  animate={{
                    scale: [1, 1.1, 1],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    repeatType: "reverse",
                    delay: 2.5
                  }}
                />
                <p className="relative z-10"><strong>Inbox full of meeting requests?</strong> Just send a link—your doubles take it from there.</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Deploy Wherever You Work Section */}
      <section className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 
            className="text-4xl font-bold text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            You Already Know Where a Double Could Help
          </motion.h2>
          
          <motion.p 
            className="text-xl text-gray-600 text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            Every conversation stays aligned, even when you're not in the room, because your doubles know the role, the tone, and the goal.
          </motion.p>

          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
            <FeatureCard
              icon={Phone}
              title="Sales"
              description="Your outbound double starts the conversation—so you step in only when they're ready to buy."
              delay={0.1}
            />
            
            <FeatureCard
              icon={Brain}
              title="Fundraising"
              description="Your double delivers your pitch and captures key investor questions—no schedule syncing required."
              delay={0.2}
            />
            
            <FeatureCard
              icon={Users}
              title="Hiring"
              description="A recruiting double screens candidates and returns structured summaries with clear next steps."
              delay={0.3}
            />
            
            <FeatureCard
              icon={Tag}
              title="Internal & Ops"
              description="Doubles walk team members through compliance, benefits, or onboarding—no repeated explanations."
              delay={0.4}
            />
          </div>
        </div>
      </section>

      {/* Setup Section */}
      <section className="py-24 bg-gray-50 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="max-w-3xl mx-auto text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.h2 
              className="text-4xl font-bold mb-6"
              variants={slideUp}
              transition={{ duration: 0.7 }}
            >
              From Zero to Double in 2 Minutes
            </motion.h2>
            
            <motion.p 
              className="text-xl text-gray-600 mb-12"
              variants={slideUp}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              You don't need training data, prompt engineering, or hours of setup. Tell us what your double is for, and it's ready to take calls within minutes.
            </motion.p>
            
            <motion.div 
              className="space-y-4 text-lg bg-white p-8 rounded-xl border-2 border-dark shadow-retro"
              variants={fadeIn}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <p className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-full flex items-center justify-center bg-primary/10 text-primary font-bold">1</span>
                Choose a purpose (sales, hiring, ops—anything)
              </p>
              <p className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-full flex items-center justify-center bg-primary/10 text-primary font-bold">2</span>
                Add tone & context
              </p>
              <p className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-full flex items-center justify-center bg-primary/10 text-primary font-bold">3</span>
                Activate your double
              </p>
              <p className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-full flex items-center justify-center bg-primary/10 text-primary font-bold">4</span>
                Your double has a number, a scheduling link, and is ready to work
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.h2 
              className="text-4xl font-bold mb-6"
              variants={slideUp}
              transition={{ duration: 0.7 }}
            >
              You Don't Have to Miss the Moment. Your Double Caught It.
            </motion.h2>
            
            <motion.p 
              className="text-xl text-gray-600 max-w-3xl mx-auto"
              variants={slideUp}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              Every conversation is summarized. Every task is sorted. Every contact is remembered…by all your doubles.
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard
              icon={CheckSquare}
              title="Triage Built‑In"
              description="Follow‑ups arrive pre‑categorized: Own, Delegate, Archive."
              delay={0.1}
            />
            
            <FeatureCard
              icon={Brain}
              title="Shared Memory"
              description="All doubles learn from each other—context deepens with every call."
              delay={0.2}
            />
            
            <FeatureCard
              icon={Calendar}
              title="Single Source of Truth"
              description="CRM updates automatically. No extra tools, no extra tabs."
              delay={0.3}
            />
          </div>
        </div>
      </section>

      {/* Scale Section */}
      <section className="py-24 bg-gray-50 relative">
        <motion.div 
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
        >
          <motion.div className="text-center max-w-3xl mx-auto">
            <motion.h2 
              className="text-4xl font-bold mb-6"
              variants={slideUp}
              transition={{ duration: 0.7 }}
            >
              Scale Isn't More Meetings. It's Fewer You Need to Take.
            </motion.h2>
            
            <motion.p 
              className="text-xl text-gray-600 mb-16"
              variants={slideUp}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              You'll never get time back. But with doubles, you don't lose it in the first place. Be in every first call, every screen, every follow‑up—without burning out.
            </motion.p>
          </motion.div>

          <motion.div 
            className="space-y-4 text-xl px-4"
            variants={fadeIn}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <motion.div 
              className="p-6 bg-white rounded-xl border-2 border-dark shadow-retro hover:shadow-none hover:-translate-y-1 transition-all"
              whileHover={{ scale: 1.02 }}
            >
              <p>• Let them screen, qualify, and summarize</p>
            </motion.div>
            <motion.div 
              className="p-6 bg-white rounded-xl border-2 border-dark shadow-retro hover:shadow-none hover:-translate-y-1 transition-all"
              whileHover={{ scale: 1.02 }}
              transition={{ delay: 0.1 }}
            >
              <p>• Let them remember, respond, and follow up</p>
            </motion.div>
            <motion.div 
              className="p-6 bg-white rounded-xl border-2 border-dark shadow-retro hover:shadow-none hover:-translate-y-1 transition-all"
              whileHover={{ scale: 1.02 }}
              transition={{ delay: 0.2 }}
            >
              <p>• Let them take the first 15 minutes—so you can crush the last 5</p>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* Audience Section */}
      <section className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.h2 
              className="text-4xl font-bold mb-8"
              variants={slideUp}
              transition={{ duration: 0.7 }}
            >
              If You're Building, Leading, or Selling—You Need a Double
            </motion.h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard
              icon={Sparkles}
              title="Founders"
              description="Raise capital, hire better, stay responsive—without being always‑on."
              delay={0.1}
            />
            
            <FeatureCard
              icon={Users}
              title="Revenue Teams"
              description="Let your best voice handle your pipeline 24/7."
              delay={0.2}
            />
            
            <FeatureCard
              icon={Brain}
              title="Execs & Operators"
              description="Scale communication without stretching yourself thin."
              delay={0.3}
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-dark -z-10" />
        <motion.div 
          className="absolute inset-0 bg-gradient-shine bg-[length:200px_200px] opacity-10 -z-10"
          animate={{
            backgroundPosition: ['0% 0%', '100% 100%']
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "mirror"
          }}
        />
        <motion.div 
          className="absolute w-[800px] h-[800px] rounded-full opacity-20 -z-10"
          animate={{
            background: [
              "radial-gradient(circle at center, rgba(69,183,135,0.3) 0%, transparent 70%)",
              "radial-gradient(circle at center, rgba(255,211,63,0.3) 0%, transparent 70%)",
              "radial-gradient(circle at center, rgba(255,155,80,0.3) 0%, transparent 70%)",
              "radial-gradient(circle at center, rgba(69,183,135,0.3) 0%, transparent 70%)",
            ]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            repeatType: "mirror"
          }}
          style={{
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            filter: "blur(70px)"
          }}
        />
        
        <motion.div 
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
        >
          <motion.div
            variants={slideUp}
            transition={{ duration: 0.7 }}
          >
            <h2 className="text-5xl font-bold mb-6 text-white">
              <span className="inline-block relative">
                <span>Start With One Double. Expand as You Grow.</span>
                <motion.div
                  className="absolute -inset-2 rounded-lg opacity-70 -z-10"
                  animate={{ 
                    scale: [1, 1.5, 1],
                    opacity: [0.5, 0, 0.5],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                  style={{
                    background: "radial-gradient(circle, rgba(255, 211, 63, 0.5) 0%, rgba(255, 211, 63, 0) 70%)"
                  }}
                />
              </span> 
            </h2>
          </motion.div>
          
          <motion.p 
            className="text-xl text-gray-300 mb-12"
            variants={slideUp}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            You don't need to scale your team just yet. Scale your voice, your presence, and your power—with one link and one call.
          </motion.p>
          
          <motion.button 
            onClick={() => setShowOnboardingModal(true)}
            className="bg-white text-dark hover:bg-gray-100 font-display px-8 py-4 rounded-full transition-all border-2 border-white shadow-retro hover:shadow-none hover:-translate-y-1 flex items-center justify-center mx-auto relative overflow-hidden group"
            variants={slideUp}
            transition={{ duration: 0.7, delay: 0.2 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            <motion.div 
              className="absolute inset-0 bg-gradient-to-r from-primary/10 via-secondary/10 to-accent/10 opacity-0 group-hover:opacity-100 transition-all duration-500"
              animate={{
                backgroundPosition: ['0% 0%', '100% 100%']
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                repeatType: "mirror"
              }}
            />
            <span className="text-lg font-bold flex items-center">
              <Sparkles className="mr-2 h-5 w-5 text-primary" />
              Build Your First Double
              <ArrowRight className="ml-2 h-5 w-5" />
            </span>
          </motion.button>
          
          <motion.div 
            className="mt-10 text-center"
            variants={fadeIn}
            transition={{ duration: 1, delay: 0.7 }}
          >
            <motion.p 
              className="text-xl text-white"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5 }}
            >
              — It takes under two minutes.
            </motion.p>
            <motion.div
              className="w-12 h-1 bg-gradient-to-r from-primary via-secondary to-accent rounded-full mx-auto mt-4"
              initial={{ width: 0, opacity: 0 }}
              whileInView={{ width: 48, opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
              viewport={{ once: true }}
            />
          </motion.div>
        </motion.div>
      </section>

      {/* Floating elements */}
      <div className="fixed bottom-8 left-8 z-40">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          whileHover={{ scale: 1.1, rotate: -5 }}
        >
          <button
            onClick={() => window.location.href = "tel:+16292309527"}
            className="bg-white shadow-lg border-2 border-dark rounded-lg p-3 flex items-center gap-3 group hover:shadow-xl transition-shadow duration-300"
          >
            <div className="relative w-6 h-6 flex items-center">
              {[...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute left-1/2 w-0.5 bg-primary"
                  style={{ height: 12 + i * 4 }}
                  animate={{
                    scaleY: [0.4, 1, 0.4],
                    opacity: [0.3, 1, 0.3]
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: i * 0.2
                  }}
                />
              ))}
            </div>
            <span className="text-sm font-medium pr-1 group-hover:text-primary transition-colors">
              Want to demo the Double Me double?
            </span>
          </button>
        </motion.div>
      </div>

      {/* Login Modal with enhanced animations */}
      {showLoginModal && (
        <motion.div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowLoginModal(false)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div 
            onClick={e => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ 
              duration: 0.4,
              type: "spring",
              stiffness: 300,
              damping: 25
            }}
            className="relative"
          >
            <motion.div
              className="absolute -inset-4 bg-gradient-to-r from-primary/20 via-secondary/20 to-accent/20 rounded-xl blur-xl -z-10"
              animate={{ 
                opacity: [0.5, 0.8, 0.5],
                rotate: [0, 5, 0],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                repeatType: "reverse"
              }}
            />
            <SignIn onClose={() => setShowLoginModal(false)} />
          </motion.div>
        </motion.div>
      )}
      
      {/* Onboarding Modal */}
      {showOnboardingModal && (
        <OnboardingModal onClose={() => setShowOnboardingModal(false)} />
      )}
      
      {/* Video Modal */}
      {showVideoModal && <VideoModal onClose={() => setShowVideoModal(false)} />}
    </div>
  );
};

export default LandingPage;